// Poll codes: 6 digits, shown as `482-193` (BACKLOG #5). People type them from
// the back of a room, so parsing forgives a missing dash, spaces, and the
// other dash characters a phone keyboard or a pasted link may produce.

export const CODE_DIGITS = 6;

const CODE_RE = /^\d{6}$/;

/** True for a normalized code: exactly 6 ASCII digits. */
export function isCode(value: string): boolean {
  return CODE_RE.test(value);
}

/** `482193` -> `482-193`. Throws on anything that is not a normalized code. */
export function formatCode(code: string): string {
  if (!isCode(code)) throw new Error(`not a poll code: ${code}`);
  return `${code.slice(0, 3)}-${code.slice(3)}`;
}

/**
 * Accepts what a person might type or paste and returns the normalized code,
 * or null. `482-193`, `482193`, `482 193`, ` 482<en dash>193 ` all give `482193`.
 * A full link (`freelivepoll.com/482-193`) gives the code from its last segment.
 */
export function parseCode(input: string): string | null {
  let s = input.trim();
  const slash = s.lastIndexOf("/");
  if (slash !== -1) s = s.slice(slash + 1);
  s = s.split(/[?#]/)[0] ?? "";
  // Spaces, ASCII hyphen, and the Unicode dashes (U+2010 to U+2015, U+2212).
  s = s.replace(/[\s\u002D\u2010-\u2015\u2212]/g, "");
  return isCode(s) ? s : null;
}

/** A random code from a source of random 32-bit integers (crypto in callers). */
export function codeFromRandom(next: () => number): string {
  // Rejection sampling keeps the distribution uniform over 000000 to 999999.
  const limit = Math.floor(0x1_0000_0000 / 1_000_000) * 1_000_000;
  let n = next() >>> 0;
  while (n >= limit) n = next() >>> 0;
  return String(n % 1_000_000).padStart(CODE_DIGITS, "0");
}
